/**
 * useHomeChildReturn — Home's consumer of a child sub-app return.
 *
 * A run launched FROM the Home workspace (e.g. a protocol from the pane-3
 * embedded chat) returns with `?from_child=<child>&parent_session=<parent>`
 * (see useSessionHandoff). This hook, mounted by Home only:
 *
 *   1. Captures + strips the params (replace, no history entry) so a reload
 *      or bookmark can't re-fire a pull for a stale child. Single-shot via a
 *      ref so StrictMode's dev double-invoke can't select + pull twice (the
 *      [] deps close over the FIRST render's unstripped params) — same idea
 *      as Session.tsx's recordedChildRef.
 *   2. Selects the parent conversation in pane 3 immediately, then CORRECTS
 *      THE SCOPE once the session list loads: Home validates the selection
 *      against the ACTIVE scope's list and silently falls back to that
 *      scope's latest on a miss, so if the persisted scope/patient drifted
 *      during the run (another tab, a long-running protocol) the nurse would
 *      land on an unrelated conversation with no signal. Steering scope +
 *      patient to the parent's own bucket (patient frame → Patienten tab;
 *      else the agent's personal/team scope — Home's own bucketing rule)
 *      makes the landing deterministic instead of silently wrong.
 *   3. Records the child's summary through the shared useRecordChildSummary
 *      — the same server-pull contract pages/Session.tsx honours. Failure
 *      surfaces as a retryable toast, never silent (frontend/CLAUDE.md);
 *      permanent denials (401/403: no parent write access for a
 *      shared-session colleague) stay quiet like Session.tsx, and the
 *      empty-child 404 already resolves at the API binding.
 *
 * Error-surface divergence from Session.tsx is INTENTIONAL: the full-screen
 * session page owns its transcript and renders the pull failure as an inline
 * retry banner in it; Home's pane 3 renders the agent's own view
 * (ChatConnected), which doesn't know about the return flow, so the failure
 * surfaces on Home's standard workspace-level error channel — the retryable
 * errorToast every other Home-level failure uses (launchAgent).
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { errorToast } from '@/hooks/errorToast'
import { useRecordChildSummary } from '@/hooks/useSessionHandoff'
import { useSessions } from '@/hooks/useSessions'
import { useAgentCatalogue } from '@/hooks/useAgentCatalogue'
import { resolveAgentIdentity } from '@/lib/agentIdentity'
import { FROM_CHILD_PARAM, RETURN_PARENT_PARAM, sessionSurfaceSlug } from '@/lib/sessionRouting'
import { useHomeNavStore } from '@/stores/homeNav'

export function useHomeChildReturn(): void {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const recordChildSummary = useRecordChildSummary()
  const setScope = useHomeNavStore((s) => s.setScope)
  const setSelectedPatientId = useHomeNavStore((s) => s.setSelectedPatientId)
  const setSelectedConversationId = useHomeNavStore((s) => s.setSelectedConversationId)
  const { data: sessions } = useSessions()
  const catalogue = useAgentCatalogue()

  // The returned parent awaiting its scope correction (step 2) — state, not a
  // ref, because the correction must re-run when the session list arrives.
  const [pendingParentId, setPendingParentId] = useState<string | null>(null)
  const handledReturnRef = useRef(false)

  useEffect(() => {
    if (handledReturnRef.current) return
    handledReturnRef.current = true
    const childSessionId = searchParams.get(FROM_CHILD_PARAM)
    const parentSessionId = searchParams.get(RETURN_PARENT_PARAM)
    if (!childSessionId && !parentSessionId) return
    const next = new URLSearchParams(searchParams)
    next.delete(FROM_CHILD_PARAM)
    next.delete(RETURN_PARENT_PARAM)
    setSearchParams(next, { replace: true })
    if (!childSessionId || !parentSessionId) return
    setSelectedConversationId(parentSessionId)
    setPendingParentId(parentSessionId)
    const record = () =>
      recordChildSummary.mutate(
        { parentSessionId, childSessionId },
        {
          onError: (err) => {
            if (err instanceof ApiError && [401, 403].includes(err.status)) return
            errorToast({
              context: 'recordChildSummary',
              description: t('chat.inboundSummary.loadError', 'Could not load the summary.'),
              onRetry: record,
            })
          },
        },
      )
    record()
    // Once at mount: the return is a full-page navigation, so the params can
    // only be present on first render. Depending on searchParams would re-run
    // after our own strip and re-fire the summary pull — mirrors Session.tsx's
    // capture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Step 2's deferred half: once the session list is in, steer scope/patient
  // to the parent's own bucket so Home's scope-validated selection can't
  // silently fall back to an unrelated conversation. A parent missing from
  // the loaded list (deleted / access revoked mid-run — or just outside the
  // list's page cap, though a parent that spawned a child moments ago sits at
  // the top of the newest-first list) keeps the pending state so a later
  // refetch can still land the correction; there is nothing better to show
  // meanwhile, since ALL of Home renders from this same list. A truly-gone
  // parent is covered by the summary pull's own error toast.
  useEffect(() => {
    if (!pendingParentId || !sessions) return
    const parent = sessions.find((s) => s.id === pendingParentId)
    if (!parent) return
    // A NON-patient parent's bucket comes from the agent catalogue; before
    // /config lands the catalogue is empty and resolveAgentIdentity falls back
    // to 'personal' — steering a team parent there would strand the nurse on
    // the wrong tab once the config arrives and the session re-buckets. Hold
    // the correction (pending survives; catalogue is a dep) until the
    // catalogue is real. Patient parents don't consult it at all.
    if (parent.subject?.type !== 'patient' && catalogue.length === 0) return
    setPendingParentId(null)
    if (parent.subject?.type === 'patient') {
      setScope('patient')
      setSelectedPatientId(parent.subject.id)
    } else {
      // Mirrors Home's bucketing: team-scoped agents → Team, else Persönlich.
      setScope(resolveAgentIdentity(sessionSurfaceSlug(parent), catalogue).scope === 'team' ? 'team' : 'personal')
    }
    setSelectedConversationId(parent.id)
  }, [pendingParentId, sessions, catalogue, setScope, setSelectedPatientId, setSelectedConversationId])
}
